<template>
  <d-container
    fluid
    class="main-content-container px-4 services-schedule-planner">
    <!-- Page Header -->
    <d-row
      no-gutters
      class="page-header py-4">
      <d-col
        col
        sm="6"
        class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('service.services') }}</span>
        <h3 class="page-title">
          {{ $t('service.schedule.planningServices') }}
        </h3>
      </d-col>
    </d-row>

    <div
      class="list-filters page-filters w-100"
      :class="{ 'loading': !allDataLoaded }">
      <form
        class="basic-filters d-flex flex-wrap align-items-center mb-2"
        @submit.prevent="debouncedLoadFilteredData">

        <div style="flex: 1 1 200px; max-width: 300px;">
          <div class="form-control is-select">
            <v-select
              class="w-100"
              :options="contractorsOptions"
              v-model="filters.contractor_id"
              :placeholder="$t('ui.selectSupplier')"
              :clearable="false"
              :reduce="contractor => contractor.value"
              :disabled="!additionalDataLoaded"
              label="text">
            </v-select>
          </div>
        </div>

        <div style="flex: 1 1 200px; max-width: 300px;">
          <div class="form-control is-select">
            <v-select
              class="w-100"
              :options="servicesTypesOptions"
              v-model="filters.service_type_id"
              :placeholder="$t('service.selectServiceType')"
              :clearable="false"
              :reduce="serviceType => serviceType.value"
              :disabled="!additionalDataLoaded"
              label="text">
            </v-select>
          </div>
        </div>

        <d-button
          theme="info"
          outline
          :disabled="!allDataLoaded"
          :title="$t('ui.search')"
          type="submit">
          <i class="material-icons">search</i>
        </d-button>

        <div
          class="d-flex flex-wrap align-items-center filter-actions ml-auto"
          style="gap: 0.75rem;">

          <d-button-group>
            <d-button
              :outline="currentView === 'week'"
              :disabled="!allDataLoaded"
              @click.prevent="setView('month')">
              {{ $t('service.schedule.monthlyView') }}
            </d-button>
            <d-button
              :outline="currentView === 'month'"
              :disabled="!allDataLoaded"
              @click.prevent="setView('week')">
              {{ $t('service.schedule.weeklyView') }}
            </d-button>
          </d-button-group>
        </div>
      </form>
    </div>

    <d-row
      class="mt-4 mb-5 mx-0 content-wrapper p-0">
      <d-alert
        v-if="loadError"
        show
        class="w-100"
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a
          href="javascript:window.location.reload();"
        class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>

      <calendar
        v-if="currentView === 'month'"
        :loadedItems="listItems"
        ref="calendar" />

      <week
        v-else-if="currentView === 'week'"
        :loadedItems="listItems"
        ref="week" />

      <div
        v-if="!allDataLoaded || orderChangeInProgress"
        class="pb-3 pt-3 text-center overlay-loading">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>
    </d-row>

    <day-popup
      v-if="showDayPopup"
      :date="dayPopupDate"
      :filters="filters" />

    <move-popup
      v-if="showMovePopup"
      :itemID="movePopupItemID"
      :maxQty="movePopupMaxQty"
      :movePopupDayPopupDate="movePopupDayPopupDate" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import Calendar from './../components/services-schedule/Calendar';
import DayPopup from './../components/services-schedule/DayPopup';
import FormUtils from './../utils/FormUtils.js';
import ListUtils from './../utils/ListUtils.js';
import MovePopup from './../components/services-schedule/MovePopup';
import Week from './../components/services-schedule/Week';
import Vue from 'vue';

export default {
  name: 'services-schedule',
  components: {
    'calendar': Calendar,
    'day-popup': DayPopup,
    'move-popup': MovePopup,
    'week': Week
  },
  computed: {
    additionalDataLoaded () {
      return this.loadedContractors && this.loadedServicesTypes;
    },
    allDataLoaded () {
      return this.loaded && this.additionalDataLoaded;
    },
    contractorsOptions () {
      return this.contractors.map(contractor => ({
        value: contractor.id,
        text: contractor.name
      }));
    },
    servicesTypesOptions () {
      return this.servicesTypes.map(serviceType => ({
        value: serviceType.id,
        text: serviceType.name
      }));
    }
  },
  watch: {
    additionalDataLoaded (newValue, oldValue) {
      if (newValue && newValue !== oldValue) {
        this.filters.contractor_id = this.contractors[0].id;
        this.filters.service_type_id = this.servicesTypes[0].id;
        this.loadFilteredData();
      }
    }
  },
  data () {
    return {
      listItems: [],
      currentView: 'month',
      contractors: [],
      servicesTypes: [],
      loaded: false,
      loadError: false,
      loadedServicesTypes: false,
      loadedContractors: false,
      filters: {
        contractor_id: null,
        service_type_id: null
      },
      // day popup variables
      showDayPopup: false,
      showMovePopup: false,
      dayPopupDate: '',
      movePopupMaxQty: '',
      movePopupItemID: [],
      movePopupDayPopupDate: false,
      // week calendar variables
      orderChangeInProgress: false
    };
  },
  beforeMount () {
    if (window.localStorage.getItem('headwear-crm-services-scheduler-current-view')) {
      let view = window.localStorage.getItem('headwear-crm-services-scheduler-current-view');

      if (view === 'week') {
        this.currentView = 'week';
      } else if (view === 'month') {
        this.currentView = 'month';
      }
    }
  },
  mounted () {
    this.loadAdditionalData();
    this.$bus.$on('services-schedule-calendar-refresh', this.debouncedLoadFilteredData);
    this.$bus.$on('services-schedule-open-day-popup', this.openDayPopup);
    this.$bus.$on('services-schedule-close-day-popup', this.closeDayPopup);
    this.$bus.$on('services-schedule-open-move-popup', this.openMovePopup);
    this.$bus.$on('services-schedule-close-move-popup', this.closeMovePopup);
    this.$bus.$on('services-schedule-update-day-popup', this.updateDayPopup);
    this.$bus.$on('services-schedule-week-order-change', this.weekDayOrderChangeInProgress);
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData (dayPopupToUpdate = false) {
      let where = {};

      if (this.filters.contractor_id !== null && this.filters.contractor_id) {
        where.contractor_id = this.filters.contractor_id;
      }

      if (this.filters.service_type_id !== null && this.filters.service_type_id) {
        where.service_type_id = this.filters.service_type_id;
      }

      if (this.currentView === 'month') {
        let period = this.$refs['calendar'].currentPeriod;
        where.date_from = period.date_from;
        where.date_to = period.date_to;
      } else if (this.currentView === 'week') {
        let period = this.$refs['week'].currentPeriod;
        where.date_from = period.date_from;
        where.date_to = period.date_to;
      }

      ListUtils.loadPaginatedItemsData(this, {
        endpoint: '/services-schedule/items',
        listField: 'listItems',
        loadedKey: 'loaded',
        params: {
          where
        },
        successAction: () => {
          if (this.currentView === 'week') {
            Vue.nextTick(() => {
              this.$bus.$emit('services-schedule-week-days-reload');
            });
          }

          if (dayPopupToUpdate) {
            let foundedItems = this.listItems.filter(items => items.date === dayPopupToUpdate);

            if (foundedItems[0]) {
              this.dayPopupItems = JSON.parse(JSON.stringify(foundedItems[0].items));

              setTimeout(() => {
                this.$bus.$emit('services-schedule-day-popup-refresh');
              }, 0);
            }
          }
        }
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/services/types',
        loadedKey: 'loadedServicesTypes',
        outputKey: 'servicesTypes',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/contractors/items',
        loadedKey: 'loadedContractors',
        outputKey: 'contractors',
        errorKey: 'loadError',
        params: {
          return: 'get'
        }
      });
    },
    openDayPopup (date) {
      this.dayPopupDate = date;

      setTimeout(() => {
        this.showDayPopup = true;
      }, 100);
    },
    closeDayPopup () {
      this.showDayPopup = false;
      this.dayPopupDate = '';
    },
    openMovePopup (itemID, maxQty, dayPopupDate = false) {
      this.movePopupItemID = itemID;
      this.movePopupMaxQty = maxQty;
      this.movePopupDayPopupDate = dayPopupDate;

      setTimeout(() => {
        this.showMovePopup = true;
      }, 100);
    },
    closeMovePopup () {
      this.showMovePopup = false;
      this.movePopupMaxQty = '';
      this.movePopupItemID = [];
      this.movePopupDayPopupDate = false;
    },
    updateDayPopup (date) {
      this.loadFilteredData(date);
    },
    setView (view) {
      Vue.set(this, 'currentView', view);
      window.localStorage.setItem('headwear-crm-services-scheduler-current-view', view);
      this.debouncedLoadFilteredData();
    },
    weekDayOrderChangeInProgress (newValue) {
      this.orderChangeInProgress = newValue;
    }
  },
  beforeDestroy () {
    this.$bus.$off('services-schedule-calendar-refresh', this.debouncedLoadFilteredData);
    this.$bus.$off('services-schedule-open-day-popup', this.openDayPopup);
    this.$bus.$off('services-schedule-close-day-popup', this.closeDayPopup);
    this.$bus.$off('services-schedule-open-move-popup', this.openMovePopup);
    this.$bus.$off('services-schedule-close-move-popup', this.closeMovePopup);
    this.$bus.$off('services-schedule-update-day-popup', this.updateDayPopup);
    this.$bus.$off('services-schedule-week-order-change', this.weekDayOrderChangeInProgress);
  }
};
</script>

<style lang="scss">
.services-schedule-planner {
  .basic-filters {
    gap: 0.75rem;
  }

  .content-wrapper {
    position: relative;

    .overlay-loading {
      left: 0;
      opacity: .5;
      top: 0;
    }
  }

  .btn-group {
    .btn {
      white-space: nowrap;
    }
  }
}
</style>
